import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Match } from "@/types/match";
import { useSearch } from "@/hooks/useSearch";
import { TeamCrest } from "./TeamCrest";
import { LiveBadge } from "./LiveBadge";

export function SearchBar() {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const { results, isSearching } = useSearch(query);
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (match: Match) => {
    setIsOpen(false);
    setQuery("");
    // Same hand-off as the match cards: pass the match we already have so
    // the detail page paints instantly instead of refetching from scratch.
    navigate(`/match/${match.id}`, { state: { prefetchedMatch: match } });
  };

  return (
    <div ref={containerRef} className="relative w-full max-w-md">
      <div className="relative">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
        </svg>
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search teams, leagues, tournaments…"
          className="w-full bg-base-800 border border-base-600 rounded-xl pl-9 pr-3 py-2.5 text-sm text-ink-100 placeholder:text-ink-700 focus:border-emerald/50 transition-colors"
        />
      </div>

      {isOpen && query.trim() && (
        <div className="absolute mt-2 w-full glass rounded-xl shadow-panel overflow-hidden z-50 animate-fade-in max-h-80 overflow-y-auto">
          {isSearching && <div className="px-4 py-3 text-sm text-ink-500">Searching…</div>}
          {!isSearching && results.length === 0 && (
            <div className="px-4 py-3 text-sm text-ink-500">No matches found for "{query}"</div>
          )}
          {!isSearching &&
            results.map((m) => (
              <button
                key={m.id}
                onClick={() => handleSelect(m)}
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors text-left"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <TeamCrest team={m.homeTeam} size="sm" />
                  <span className="text-sm text-ink-100 truncate">
                    {m.homeTeam.shortName} vs {m.awayTeam.shortName}
                  </span>
                </div>
                {m.status === "LIVE" ? (
                  <LiveBadge minute={m.minute ? `${m.minute}'` : undefined} />
                ) : (
                  <span className="text-xs text-ink-500 shrink-0 ml-2">{m.league.shortName}</span>
                )}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
